/* eslint-disable no-shadow */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import cx from 'classnames';
import pickBy from 'lodash/pickBy';
import isEqual from 'lodash/isEqual';
import { loadResyConfigThunk } from '../../../reducers/resyConfigSlice';
import { getUserThunk, setUpdatedPhoneNumber } from '../../../reducers/userSlice';
import { IDLE, asyncLoadingStatusTypes } from '../../../constants/asyncLoadingStatuses';
import TranslationProvider, { getTranslation } from '../../../providers/TranslationProvider';
import { SubmitButton } from '../../Button';
import RouterNavigateContext from '../../../../../widget/app/modules/contexts/RouterNavigate';

import {
  FirstNameInput,
  LastNameInput,
  EmailInput,
  PhoneInput,
} from '../../Input';
import ProfileCard, { PROFILE_CARD_PAGE_CONTEXT } from '../ProfileCard/ProfileCard';
import TextArea from '../../TextArea';
import Loader from '../../Loader';
import parsePhone from '../../../utils/parsePhone';
import Pill from '../../Pill';
import DateInput, { EMPTY_DATE, INCOMPLETE_DATE, dateToApiDate } from '../../DateInput';
import GreenCheckIcon from '../../../svg/icons/check.svg';
import ErrorIcon from '../../../svg/icons/error.svg';
import AccountLabel from '../AccountLabel';
import AccountConcierge from '../AccountConcierge/AccountConcierge';
import { FormErrorInline } from '../../Form';
import './AccountProfile.scss';
import '../AccountPage.scss';
import {
  deleteDietaryNote,
  getConsumerGuestTags,
  getTags,
  getUserProfileConciergeRegistration,
  postConsumerGuestTags,
  postDietaryNote,
  postDietaryNotesList,
  postUserProfileConciergeRegistration,
  postUserWithAuth,
} from '../../../api/api';
import Logger from '../../../utils/logger';
import { handleNavigate } from '../../../utils/locationUtils';
import { isHtml5EmailValid, sanitizeEmailInput } from '../../../utils/emailValidation';

export function statesFromAPIResponse({
  resUser, resTags, allergyText, userId,
}) {
  const user = resUser || {};
  const restrictions = resTags || {};
  const tags = restrictions.tags || [];

  const allergyIds = tags.reduce(
    (result, item) => {
      if (item.group_id === 1) result.push(item.id);
      return result;
    }, []
  );

  const dietRestrictionIds = tags.reduce(
    (result, item) => {
      if (item.group_id === 10) result.push(item.id);
      return result;
    }, []
  );

  const userClone = {
    firstName: user.first_name || '',
    lastName: user.last_name || '',
    dateOfBirth: user.date_of_birth || EMPTY_DATE,
    mobileNumber: user.mobile_number || '',
    emAddress: user.em_address || '',
    bio: user.bio || '',
    profileImageUrl: user.profile_image_url,
    isGda: user.is_global_dining_access,
    joinDate: user.resy_member_since,
    totalBookings: user.num_bookings,
    notifyWins: user.notify_wins,
  };
  const savedState = {
    allergyIds: [...allergyIds],
    allergyText: allergyText?.note.body,
    allergyTextId: allergyText?.note.id,
    dietRestrictionIds: [...dietRestrictionIds],
    userId,
    ...userClone,
  };
  const nextState = {
    allergyIds: [...allergyIds],
    allergyText: allergyText?.note.body,
    allergyTextId: allergyText?.note.id,
    dietRestrictionIds: [...dietRestrictionIds],
    ...userClone,
    isSaving: false,
    error: null,
    formErrors: {},
    incompleteBirthday: false,
    invalidBirthday: false,
  };
  return {
    savedState,
    nextState,
  };
}

const userToFormData = ({
  firstName,
  lastName,
  dateOfBirth,
  mobileNumber,
  emAddress,
  bio,
}, shouldIncludeBirthday) => {
  const payload = new FormData();
  payload.append('first_name', firstName);
  payload.append('last_name', lastName);
  if (shouldIncludeBirthday) {
    payload.append('date_of_birth', dateOfBirth);
  }
  payload.append('mobile_number', mobileNumber);
  payload.append('em_address', emAddress);
  payload.append('bio', bio);

  return payload;
};

const renderSavingMessage = () => (
  <div className="AccountProfile__hidden">
    <TranslationProvider translationKey="saving_wait">
      <div aria-live="polite" role="alert">Saving. Please wait...</div>
    </TranslationProvider>
  </div>
);

const accountProfilePropTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func.isRequired,
    matchMedia: PropTypes.shape({
      xs: PropTypes.bool,
      sm: PropTypes.bool,
      md: PropTypes.bool,
      lg: PropTypes.bool,
      ipadLandscape: PropTypes.bool,
      xl: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  context: PropTypes.string.isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  flushRoute: PropTypes.func,
  configStatus: asyncLoadingStatusTypes.isRequired,
  countryCodes: PropTypes.arrayOf(PropTypes.shape({})),
  loadResyConfigThunk: PropTypes.func.isRequired,
  getUserThunk: PropTypes.func.isRequired,
  storeNotifyWins: PropTypes.number,
  storeTotalBookings: PropTypes.number,
};

class AccountProfile extends Component {
  static propTypes = accountProfilePropTypes;

  static contextType = RouterNavigateContext;

  static defaultProps = {
    countryCodes: undefined,
    flushRoute: () => {},
    storeNotifyWins: undefined,
    storeTotalBookings: undefined,
  }

  state = {
    formErrors: {},
  };

  savedState = null;

  allergies = [];

  dietRestrictions = [];

  componentDidMount() {
    const {
      configStatus,
      getUserThunk,
      loadResyConfigThunk,
    } = this.props;

    const resyConfigPromise = configStatus === IDLE
      ? () => loadResyConfigThunk()
      : () => Promise.resolve();
    this.loadingPromise = Promise.all([
      resyConfigPromise(),
      getUserThunk(true, { logoutOnError: false, scheduleFollowUp: true }),
      getUserProfileConciergeRegistration(),
      getConsumerGuestTags(),
      this.getDietaryNotes(),
      getTags(),
    ]).then(([, user, concierge, userTags, dietaryNote, allTags]) => {
      this.allergyTags = allTags.tags?.filter(
        (item) => item.category_id === 1 && item.group_id === 1
      );

      this.dietRestrictionTags = allTags.tags?.filter(
        (item) => item.category_id === 1 && item.group_id === 10
      );

      const { savedState, nextState } = statesFromAPIResponse({
        resUser: user, resTags: userTags, allergyText: dietaryNote, userId: dietaryNote.userId,
      });
      this.savedState = savedState;

      if (user?.is_concierge) {
        nextState.conciergeStatus = 'accepted';
      } else if (concierge?.status === 'pending') {
        nextState.conciergeStatus = 'pending';
      }

      this.setState(nextState);
    }).catch((e) => {
      Logger.error('ERROR: [AccountProfile] API failure', e);
      this.setState({ error: 'initial-load' });
    });
  }

  async getDietaryNotes() {
    const { getUserThunk } = this.props;

    try {
      const user = await getUserThunk();

      const userId = user.guest_id;
      const response = await postDietaryNotesList({ id: userId });
      const notesLength = response.entity.notes.length;
      if (notesLength > 0) {
        return {
          note: response.entity.notes[notesLength - 1],
          userId,
        };
      }
      return {
        note: {},
        userId,
      };
    } catch (error) {
      Logger.error('[AccountProfile] API failure postDietaryNotesList: ', error);
      return {
        note: [],
      };
    }
  }

  getCountryCode() {
    const {
      mobileNumber,
    } = this.state;
    const { countryCode } = parsePhone(`${mobileNumber}`);
    return countryCode;
  }

  getPhoneNumber() {
    const {
      mobileNumber,
      phoneValue,
    } = this.state;
    const { nationalFormat } = parsePhone(`${mobileNumber}`);
    const phoneNumber = mobileNumber === this.savedState.mobileNumber
      ? nationalFormat
      : phoneValue;
    return phoneNumber;
  }

  getMissingFields() {
    const { mobileNumber, phoneValue } = this.state;
    const requiredFields = ['firstName', 'lastName', 'dateOfBirth', 'mobileNumber', 'emAddress'];

    return (
      requiredFields.filter((key) => {
        const { [key]: stateVal } = this.state;
        if (key === 'mobileNumber' && this.savedState.mobileNumber !== mobileNumber && !phoneValue) return true;
        return !stateVal;
      })
    );
  }

  saveClicked = (evt) => {
    evt.preventDefault();
    const { Analytics } = this.props;
    const {
      allergyIds,
      dietRestrictionIds,
      dateOfBirth,
      emAddress,
      conciergeCompany,
      conciergeReason,
    } = this.state;

    const {
      userId,
    } = this.savedState;

    const birthdayForApi = dateToApiDate(this.savedState.dateOfBirth, dateOfBirth);
    const incompleteBirthday = birthdayForApi === INCOMPLETE_DATE;
    const invalidBirthday = !incompleteBirthday && birthdayForApi && !moment(birthdayForApi, 'YYYY-MM-DD').isValid();
    if (incompleteBirthday || invalidBirthday) {
      this.setState({
        incompleteBirthday,
        invalidBirthday,
      });
      window.scrollTo(0, 0);
      return;
    }

    const payload = userToFormData(this.state, !!birthdayForApi);
    const emailChangedTo = (emAddress === this.savedState.emAddress) ? null : emAddress;

    this.setState({ isSaving: true, saveSucceeded: false });
    const promises = [
      postUserWithAuth(payload),
      postConsumerGuestTags({ allergyIds, dietRestrictionIds }),
      this.saveDietaryNote(),
    ];
    const submittingConciergeRequest = conciergeCompany || conciergeReason;
    if (submittingConciergeRequest) {
      promises.push(postUserProfileConciergeRegistration({
        company: conciergeCompany || '',
        reason: conciergeReason || '',
      }));
    }

    this.savingPromise = Promise.all(promises)
      .then(([user, userTags, updatedAllergyText]) => {
        if (submittingConciergeRequest) {
          this.setState({
            conciergeStatus: 'pending',
            conciergeCompany: null,
            conciergeReason: null,
          });
        }
        return [user, userTags, updatedAllergyText];
      })
      .then(([user, userTags, updatedAllergyText]) => {
        const { savedState, nextState } = statesFromAPIResponse({
          resUser: user, resTags: userTags, allergyText: updatedAllergyText, userId,
        });

        const allKeysChanged = Object.keys(pickBy(savedState, (newValue, oldValue) => !isEqual(this.savedState[oldValue], newValue)));
        this.savedState = savedState;
        this.setState({ ...nextState, emailChangedTo, saveSucceeded: true });
        window.scrollTo(0, 0);

        if (allKeysChanged.length > 0) {
          Analytics.event('update profile', {
            field: allKeysChanged,
          });
        }
      })
      .catch((err) => {
        Logger.error('ERROR: [AccountProfile] API failure', err);
        const formErrors = ((err || {}).data || {}).data;
        this.setState({
          isSaving: false,
          error: 'save',
          incompleteBirthday: false,
          invalidBirthday: false,
          formErrors,
        });
        window.scrollTo(0, 0);
      });
  }

  handleToggleDietary = ({ value }, stateKey) => {
    const {
      [stateKey]: selectedIds,
    } = this.state;
    const isSelected = selectedIds.includes(value);
    const nextSelectedIds = isSelected ? selectedIds.filter((v) => v !== value) : [...selectedIds, value];
    this.setState({
      [stateKey]: nextSelectedIds,
    });
  }

  handleToggleAllergy = (item) => {
    this.handleToggleDietary(item, 'allergyIds');
  }

  handleToggleDietRestriction = (item) => {
    this.handleToggleDietary(item, 'dietRestrictionIds');
  }

  handleNameInputChange = (stateKey, errorKey, val) => {
    const { formErrors, [stateKey]: stateVal } = this.state;
    const nextVal = (val || '').trim();
    if (nextVal !== stateVal) {
      this.setState({
        [stateKey]: nextVal,
        formErrors: {
          ...formErrors,
          [errorKey]: !(/^[A-Za-z -]+$/.test(nextVal)),
        },
      });
    }
  };

  handleFirstNameChange = ({ first_name: firstName }) => {
    this.handleNameInputChange('firstName', 'first_name', firstName);
  }

  handleLastNameChange = ({ last_name: lastName }) => {
    this.handleNameInputChange('lastName', 'last_name', lastName);
  }

  handleEmailChange = ({ email: val }) => {
    const { formErrors, emAddress: stateVal } = this.state;
    const nextVal = sanitizeEmailInput(val || '');
    if (nextVal !== stateVal) {
      this.setState({
        emAddress: nextVal,
        formErrors: {
          ...formErrors,
          em_address: !!nextVal && !isHtml5EmailValid(nextVal),
        },
      });
    }
  }

  handleConciergeCompanyChange = ({ accountConciergeCompany }) => {
    this.setState({ conciergeCompany: accountConciergeCompany });
  };

  handleConciergeReasonChange = ({ accountConciergeReason }) => {
    this.setState({ conciergeReason: accountConciergeReason });
  };

  phoneNumberUpdated = (number) => {
    if (number) {
      setUpdatedPhoneNumber(number);
      const { e164, nationalFormat } = parsePhone(number);
      this.setState({ phoneValue: nationalFormat, mobileNumber: e164 });
    }
  }

  handleWidgetEditPhoneClick = () => {
    const { flushRoute } = this.props;
    const navigate = this.context;
    handleNavigate('update-phone', navigate, flushRoute);
  }

  getInlineErrorMessage = (fieldname) => {
    const formattedFieldName = (fieldname === 'emAddress') ? 'email address' : fieldname.replace(/([A-Z])/g, ' $1').toLowerCase();
    const errorString = getTranslation({
      translationKey: `errors.${formattedFieldName}`,
      fallback: `Please enter a valid ${formattedFieldName}`,
    });

    return (
      <FormErrorInline id={`${fieldname}_error`}>
        {errorString}
      </FormErrorInline>
    );
  }

  hasUnsavedUserChanges() {
    const { conciergeCompany, conciergeReason } = this.state;
    if (conciergeCompany || conciergeReason) {
      return true;
    }
    const userStateKeys = ['firstName', 'lastName', 'dateOfBirth', 'mobileNumber', 'emAddress', 'bio'];
    return (
      userStateKeys.filter((key) => {
        const { [key]: stateVal } = this.state;
        return stateVal !== this.savedState[key];
      }).length > 0
    );
  }

  hasUnsavedDietaryChanges() {
    const {
      allergyIds,
      allergyText,
      dietRestrictionIds,
    } = this.state;
    return (
      (allergyIds.sort().join('\t') !== this.savedState.allergyIds.sort().join('\t'))
      || (dietRestrictionIds.sort().join('\t') !== this.savedState.dietRestrictionIds.sort().join('\t'))
      || (allergyText !== this.savedState.allergyText)
    );
  }

  hasUnsavedChanges() {
    return this.hasUnsavedDietaryChanges() || this.hasUnsavedUserChanges();
  }

  async saveDietaryNote() {
    const { allergyText } = this.state;
    const { allergyTextId, userId } = this.savedState;

    if (allergyText?.length > 0) {
      try {
        const response = await postDietaryNote({ noteId: allergyTextId, body: allergyText, id: userId });
        return {
          note: {
            body: response.note.body,
            id: response.note.id,
          },
        };
      } catch (error) {
        Logger.error('[AccountProfile] API failure postDietaryNote: ', error);
      }
    } else if (allergyTextId) {
      try {
        await deleteDietaryNote({ noteId: allergyTextId, entityId: userId });
      } catch (error) {
        Logger.error('[AccountProfile] API failure deleteDietaryNote: ', error);
      }
    }

    return {
      note: {},
    };
  }

  render() {
    const isLoading = !this.savedState;
    const {
      allergyIds,
      allergyText,
      bio,
      conciergeStatus,
      dateOfBirth,
      dietRestrictionIds,
      emAddress,
      emailChangedTo,
      error,
      firstName,
      formErrors,
      incompleteBirthday,
      invalidBirthday,
      isGda,
      isSaving,
      joinDate,
      lastName,
      mobileNumber,
      notifyWins,
      profileImageUrl,
      saveSucceeded,
      totalBookings,
    } = this.state;
    const {
      countryCodes, $rootScope, storeNotifyWins, storeTotalBookings,
    } = this.props;
    const isMobile = !$rootScope?.matchMedia?.sm === true;
    const titleHeading = (
      <h2 className="AccountPage__title">
        <TranslationProvider translationKey="profile">
          <span>Profile</span>
        </TranslationProvider>
      </h2>
    );

    const mappableFieldErrorKeys = ['first_name', 'last_name', 'mobile_number', 'em_address', 'date_of_birth'];
    const hasMappedFormErrors = mappableFieldErrorKeys.some((key) => !!formErrors?.[key]);

    if (error && !hasMappedFormErrors) {
      return (
        <div className="AccountProfile AccountPage">
          {titleHeading}
          <div aria-live="polite" role="alert" className="AccountPage__message AccountPage__message--error">
            <TranslationProvider translationKey="errors.parse.refresh">
              <span>
                Error. Please refresh and try again.
              </span>
            </TranslationProvider>
          </div>
        </div>
      );
    }
    if (isLoading) {
      return (
        <div className="AccountProfile">
          {titleHeading}
          <Loader className="AccountPage__loader" />
        </div>
      );
    }

    let saveSuccessMessage;
    let requiredFieldsMissingMessage;
    let incompleteBirthdayMessage;
    let invalidBirthdayMessage;
    const missingFields = this.getMissingFields();
    const hasRequiredFields = missingFields.length === 0;
    const hasEmailError = !!formErrors.em_address || missingFields.includes('emAddress');
    const hasFirstNameError = !!formErrors.first_name || missingFields.includes('firstName');
    const hasLastNameError = !!formErrors.last_name || missingFields.includes('lastName');
    const hasDateOfBirthError = !!formErrors.date_of_birth || invalidBirthday || incompleteBirthday;

    if (incompleteBirthday) {
      incompleteBirthdayMessage = (
        <div
          className="AccountPage__message AccountPage__message--error"
          aria-live="polite"
          role="alert"
        >
          <i className="ResyIcon ResyIcon--check" aria-hidden="true">
            <ErrorIcon />
          </i>
          <TranslationProvider translationKey="errors.birthday.incomplete">
            <span>Please enter a complete birthday</span>
          </TranslationProvider>
        </div>
      );
    }

    if (invalidBirthday) {
      invalidBirthdayMessage = (
        <div
          className="AccountPage__message AccountPage__message--error"
          aria-live="polite"
          role="alert"
        >
          <i className="ResyIcon ResyIcon--check" aria-hidden="true">
            <ErrorIcon />
          </i>
          <TranslationProvider translationKey="errors.birthday.invalid">
            <span>Please enter a valid birthday</span>
          </TranslationProvider>
        </div>
      );
    }

    if (saveSucceeded && !this.hasUnsavedChanges()) {
      const isMultiline = Boolean(emailChangedTo);
      saveSuccessMessage = (
        <div
          className={cx(
            'AccountPage__message',
            'AccountPage__message--success',
            { 'AccountPage__message--multiline': isMultiline }
          )}
          aria-live="polite"
          role="alert"
        >
          <i className="ResyIcon ResyIcon--check" aria-hidden="true">
            <GreenCheckIcon />
          </i>
          {emailChangedTo && (
            <TranslationProvider
              translationKey="profile_success_email"
              translationValues={{ email: emailChangedTo }}
            >
              <span className="AccountPage__message-text">
                {`Thanks, a confirmation email was sent to ${emailChangedTo}.`}
                <br />
                <span style={{ fontWeight: 'normal' }}>Your email will not be changed until you confirm.</span>
              </span>
            </TranslationProvider>
          )}
          {!emailChangedTo && (
            <TranslationProvider translationKey="profile_success">
              <span className="AccountPage__message-text">Thanks, your profile has been saved.</span>
            </TranslationProvider>
          )}
        </div>
      );
    }

    return (
      <div
        className={
          cx('AccountProfile AccountPage', {
            AccountProfile__RGA: !!isGda,
          })
        }
      >
        <ProfileCard
          context={PROFILE_CARD_PAGE_CONTEXT.PROFILE}
          joinDate={joinDate}
          // Counts come from the store when available so a follow-up /2/user fetch
          // after a reservation change updates them; form fields stay in local state
          reservations={storeTotalBookings ?? totalBookings}
          notifyWins={storeNotifyWins ?? notifyWins}
          isGDAUser={isGda}
          isMobile={isMobile}
          imageURL={profileImageUrl}
          user={{
            bio,
            emAddress,
            firstName,
            lastName,
            mobileNumber,
          }}
        />
        {titleHeading}
        <TranslationProvider translationKey="profile_subtitle">
          <p className="AccountPage__subtitle">We only share this information with restaurants you reserve, so they can coordinate with you and know your preferences.</p>
        </TranslationProvider>
        {saveSuccessMessage}
        {incompleteBirthdayMessage || invalidBirthdayMessage || requiredFieldsMissingMessage}
        <form noValidate className="AccountPage__form" onSubmit={this.saveClicked}>
          <div className="AccountProfile">
            <div className="AccountProfile__body">
              <div className="AccountProfile__row">
                <div className="AccountProfile__row-description">
                  <TranslationProvider translationKey="required_field">
                    Required fields marked with an asterisk (*).
                  </TranslationProvider>
                </div>
              </div>
              <div className="AccountProfile__row AccountProfile__row--names" data-dd-privacy="mask">
                <div className="AccountProfile__name-field AccountProfile__first-name-field">
                  <AccountLabel
                    error={hasFirstNameError}
                    translationKey="first_name"
                    forInput="first_name"
                    required
                  >
                    First Name
                  </AccountLabel>
                  <FirstNameInput
                    value={firstName}
                    onChange={this.handleFirstNameChange}
                    errors={formErrors.first_name ? ['first_name'] : []}
                    ariaRequired
                  />
                  {hasFirstNameError && (this.getInlineErrorMessage('firstName'))}
                </div>
                <div className="AccountProfile__name-field">
                  <AccountLabel
                    error={hasLastNameError}
                    translationKey="last_name"
                    forInput="last_name"
                    required
                  >
                    Last Name
                  </AccountLabel>
                  <LastNameInput
                    value={lastName}
                    onChange={this.handleLastNameChange}
                    errors={formErrors.last_name ? ['last_name'] : []}
                    ariaRequired
                  />
                  {hasLastNameError && (this.getInlineErrorMessage('lastName'))}
                </div>
              </div>
              <div className="AccountProfile__row" data-dd-privacy="mask">
                <AccountLabel
                  error={!!formErrors.mobile_number || missingFields.includes('mobileNumber')}
                  translationKey="mobile_number"
                  forInput="phone"
                  required
                >
                  Mobile Number
                </AccountLabel>
                <PhoneInput
                  onChange={() => {}}
                  countryCodes={countryCodes}
                  defaultCountryCode={this.getCountryCode()}
                  phone={this.getPhoneNumber()}
                  errors={formErrors.mobile_number ? ['phone'] : []}
                  disabled
                />
                {missingFields.includes('mobileNumber') && (this.getInlineErrorMessage('mobileNumber'))}
                <TranslationProvider translationKey="mobile_number_edit">
                  <button
                    className="AccountProfile__edit_mobile"
                    onClick={() => {
                      const { context } = this.props;
                      if (context === 'widget') {
                        this.handleWidgetEditPhoneClick();
                      } else {
                        $rootScope.$broadcast('MODAL', 'update-phone', { successCallback: this.phoneNumberUpdated });
                      }
                    }}
                    type="button"
                    data-test-id="account-mobile-change-button"
                    aria-label={getTranslation({
                      translationKey: 'mobile_number_edit',
                      fallback: 'Edit Mobile Number',
                    })}
                  >
                    Edit Mobile Number
                  </button>
                </TranslationProvider>
                <div className="AccountProfile__row-description">
                  <TranslationProvider translationKey="mobile_number_note">
                    We will use only this number to confirm and manage your reservation.
                  </TranslationProvider>
                </div>
              </div>
              <div className="AccountProfile__row" data-dd-privacy="mask">
                <AccountLabel
                  error={hasEmailError}
                  translationKey="email_address"
                  forInput="email"
                  required
                >
                  Email Address
                </AccountLabel>
                <EmailInput
                  name="AccountProfile__email"
                  value={emAddress}
                  onChange={this.handleEmailChange}
                  errors={formErrors.em_address ? ['email'] : []}
                  ariaRequired
                />
                {hasEmailError && (this.getInlineErrorMessage('emAddress'))}
              </div>
              <div
                className="AccountProfile__row"
                role="group"
                aria-describedby="birthday-label"
              >

                <fieldset>
                  <legend>
                    <AccountLabel
                      id="birthday-label"
                      error={hasDateOfBirthError}
                      translationKey="birthday"
                    >
                      Birthday
                    </AccountLabel>
                  </legend>
                  <DateInput
                    value={dateOfBirth.split('-')}
                    onChange={(dateParts) => {
                      this.setState({
                        dateOfBirth: dateParts.join('-'),
                      });
                    }}
                  />
                  {(incompleteBirthday || invalidBirthday) && (
                    <FormErrorInline id="birthday_error">
                      {incompleteBirthday ? (
                        <TranslationProvider translationKey="errors.birthday.incomplete">
                          <span>Please enter a complete birthday</span>
                        </TranslationProvider>
                      ) : (
                        <TranslationProvider translationKey="errors.birthday.invalid">
                          <span>Please enter a valid birthday</span>
                        </TranslationProvider>
                      )}
                    </FormErrorInline>
                  )}
                  {!incompleteBirthday && !invalidBirthday && !!formErrors.date_of_birth && (
                    typeof formErrors.date_of_birth === 'string'
                      ? (
                        <FormErrorInline id="dateOfBirth_error">
                          {formErrors.date_of_birth}
                        </FormErrorInline>
                      )
                      : this.getInlineErrorMessage('dateOfBirth')
                  )}
                </fieldset>
                <div className="AccountProfile__row-description">
                  <TranslationProvider translationKey="birthday_note">
                    Let restaurants know when you’re celebrating.
                  </TranslationProvider>
                </div>
              </div>
              <div className="AccountProfile__row">
                <AccountLabel
                  error={false}
                  translationKey="short_bio"
                  forInput="bio"
                >
                  Short Bio
                </AccountLabel>
                <TextArea
                  value={bio}
                  name="bio"
                  onChange={(response) => {
                    this.setState({ bio: response.bio });
                  }}
                  placeholder={getTranslation({
                    translationKey: 'bio_placeholder',
                    translationValues: null,
                    fallback: 'Tell restaurants a little bit about yourself…',
                  })}
                />
              </div>
              <div className="AccountProfile__row">
                <AccountLabel
                  error={false}
                  translationKey="allergies_and_restrictions"
                  id="Allergies-and-Restrictions"
                >
                  Allergies and Restrictions
                </AccountLabel>
                <div
                  className="AccountProfile__dietary-pills"
                  role="group"
                  aria-label={getTranslation({
                    translationKey: 'allergies',
                    fallback: 'Allergies',
                  })}
                >
                  {this.allergyTags.map(({ name: labelText, id: value }) => {
                    const isSelected = allergyIds.includes(value);
                    return (
                      <Pill
                        key={value}
                        item={{
                          isSelected,
                          labelText,
                          value,
                        }}
                        onClick={this.handleToggleAllergy}
                      />
                    );
                  })}
                </div>
                <div
                  className="AccountProfile__dietary-pills"
                  role="group"
                  aria-label={getTranslation({
                    translationKey: 'restrictions',
                    fallback: 'Restrictions',
                  })}
                >
                  {this.dietRestrictionTags.map(({ name: labelText, id: value }) => {
                    const isSelected = dietRestrictionIds.includes(value);
                    return (
                      <Pill
                        key={value}
                        item={{
                          isSelected,
                          labelText,
                          value,
                        }}
                        onClick={this.handleToggleDietRestriction}
                      />
                    );
                  })}
                </div>
              </div>
              <div className="AccountProfile__row">
                <AccountLabel
                  error={false}
                  translationKey="other_notes"
                  id="Other-Allergies-and-Restrictions-Label"
                >
                  Notes
                </AccountLabel>
                <TextArea
                  name="allergyText"
                  className="AccountProfile__allergy-notes"
                  value={allergyText || ''}
                  placeholder={getTranslation({
                    translationKey: 'add_note',
                    fallback: 'Add an optional note on allergies and restrictions...',
                  })}
                  aria-labelledby="Other-Allergies-and-Restrictions-Label"
                  onChange={(response) => {
                    this.setState({ allergyText: response.allergyText });
                  }}
                />
                <div className="AccountProfile__row-description">
                  <TranslationProvider translationKey="allergy_note_disclosure_guest">
                    We will share allergies, diet restrictions, or preferences you provide with our restaurants.
                  </TranslationProvider>
                </div>
              </div>
              <AccountConcierge
                status={conciergeStatus}
                onCompanyChange={this.handleConciergeCompanyChange}
                onReasonChange={this.handleConciergeReasonChange}
                isViaConciergeURL={/concierge/.test(window.location.href)}
              />
              {isSaving && renderSavingMessage()}
              <SubmitButton
                disabled={isSaving || !hasRequiredFields || !this.hasUnsavedChanges()}
                lg
                processing={isSaving}
              >
                <TranslationProvider translationKey="save">
                  <span>Save</span>
                </TranslationProvider>
              </SubmitButton>
            </div>
          </div>
        </form>
      </div>
    );
  }
}

AccountProfile.displayName = 'AccountProfile';

const mapStateToProps = ({ resyConfig, user }) => ({
  configStatus: resyConfig.configStatus,
  countryCodes: resyConfig.config?.country_codes,
  storeNotifyWins: user.userData?.notify_wins,
  storeTotalBookings: user.userData?.num_bookings,
  userGuestId: user.userData?.guest_id,
});
const mapDispatchToProps = {
  getUserThunk,
  loadResyConfigThunk,
};

export { accountProfilePropTypes, userToFormData, AccountProfile as ComponentUnderTest };
export default connect(mapStateToProps, mapDispatchToProps)(AccountProfile);
